import { createServerFn } from "@tanstack/react-start";
import { generateText } from "ai";
import { z } from "zod";
import { Effect } from "effect";
import { authMiddleware } from "~/lib/middlewares/auth-middleware";
import generateExampleSentencePrompt from "~/data/prompts/utils/generate-example-sentence.md?raw";
import {
	buildSystemPrompt,
	mainChatModel,
	renderPrompt,
	translationModel,
} from "../ai";

const sentenceExampleGenerationSchema = z.object({
	japanese: z.string(),
	english: z.string(),
});

export type ExampleSentence = {
	sentence: string;
	source: string;
	translation: string;
};

export const generateExampleSentenceUsingAi = createServerFn({ method: "POST" })
	.middleware([authMiddleware])
	.validator(
		z.object({
			word: z.string(),
			readings: z.array(z.string()),
			meaning: z.object({
				meaning: z.string(),
				example: z.string(),
			}),
			level: z.string(),
		}),
	)
	.handler(({ data }) =>
		Effect.gen(function* () {
			const prompt = renderPrompt(generateExampleSentencePrompt, {
				word: data.word,
				readings: data.readings.join(", "),
				meaning: data.meaning.meaning,
				example: data.meaning.example,
				level: data.level,
			});

			const { text } = yield* Effect.tryPromise(() =>
				generateText({
					model: mainChatModel,
					prompt,
					temperature: 1.5,
				}),
			);

			const parsed = sentenceExampleGenerationSchema.safeParse(
				JSON.parse(
					// TODO: find a way to extract the json, maybe use xml instead?
					//       the AI is so stubborn when using temperature of 2
					text
						.replaceAll("```json", "")
						.replaceAll("```", ""),
				),
			);

			if (!parsed.success) {
				return yield* Effect.fail(new Error("Invalid response format from AI"));
			}

			return {
				sentence: parsed.data?.japanese || "",
				source: "AI",
				translation: parsed.data?.english || "",
			} satisfies ExampleSentence;
		}).pipe(
			Effect.withSpan("generateExampleSentenceUsingAi"),
			Effect.catchAll((error) =>
				Effect.succeed({
					error:
						error instanceof Error
							? error.message
							: "Failed to generate example sentence",
				}),
			),
			Effect.runPromise,
		),
	);

const generatedMassifSentenceSchema = z.object({
	hits: z.number(),
	hits_limited: z.boolean(),
	results: z.array(
		z.object({
			highlighted_html: z.string(),
			sample_source: z.object({
				publish_date: z.coerce.date(),
				title: z.string(),
				url: z.string().url(),
			}),
			source_count: z.number(),
			text: z.string(),
		}),
	),
});

const translatedSentenceSchema = z.array(
	z.object({
		sentence: z.string(),
		source: z.string().url(),
		translation: z.string(),
	}),
);

export const generateExampleSentenceFromMassif = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.validator(
		z.object({
			word: z.string(),
			count: z.number().min(1).max(10).default(1),
		}),
	)
	.handler(({ data }) =>
		Effect.gen(function* () {
			const response = yield* Effect.tryPromise(() =>
				fetch(`https://massif.la/ja/search?q=${data.word}&fmt=json`),
			);

			const raw = yield* Effect.tryPromise(() => response.json());

			const parsed = generatedMassifSentenceSchema.safeParse(raw);
			if (!parsed.success) {
				return yield* Effect.fail(
					new Error("Failed to get from external source"),
				);
			}

			const results = Array.from(
				{ length: data.count },
				() =>
					parsed.data.results[
						Math.floor(Math.random() * parsed.data.results.length)
					],
			);

			const geminiPayload = results.map((r) => ({
				sentence: r.text,
				source: r.sample_source.url,
				translations: [],
			}));

			const { text: translatedJson } = yield* Effect.tryPromise(() =>
				generateText({
					model: translationModel,
					system: buildSystemPrompt({
						focus: "translation-single",
					}),
					temperature: 0,
					prompt: JSON.stringify(geminiPayload),
				}),
			);

			const translationResult = translatedSentenceSchema.safeParse(
				JSON.parse(translatedJson),
			);

			if (!translationResult.success) {
				return yield* Effect.fail(
					new Error("Failed to get from external source"),
				);
			}

			return translationResult.data satisfies ExampleSentence[];
		}).pipe(
			Effect.withSpan("generateExampleSentenceFromMassif"),
			Effect.catchAll((error) =>
				Effect.succeed({
					error: error instanceof Error ? error.message : "Unknown error",
				}),
			),
			Effect.runPromise,
		),
	);
